import { useCallback, useEffect, useState } from 'react';
import {
  detectInstallPlatform,
  readPwaStandalone,
  type BeforeInstallPromptEvent,
  type InstallPlatform,
} from '../utils/pwa-install';

export function usePwaInstall() {
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>('desktop');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    setInstalled(readPwaStandalone());
    setPlatform(detectInstallPlatform(window.navigator.userAgent, window.navigator.maxTouchPoints));

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === 'accepted';
  }, [deferredPrompt]);

  return {
    installed,
    platform,
    canPrompt: Boolean(deferredPrompt),
    promptInstall,
  };
}
